import { modals } from "@mantine/modals";
import { Box, Text } from "@mantine/core";
import cx from "clsx";
import commonClasses from "./common.module.css";
import { IconBuilding } from "@tabler/icons-react";
import { ICompany } from "./types";


export const openCompanySelectionModal = (
  modalId: string,
  activeCompany: ICompany,
  companies: Array<ICompany>,
  onSelect: (company: ICompany) => void
) => {
  modals.open({
    modalId,
    title: "Select Company",
    size: "md",
    radius: "md",
    children: (
      <Box>
        {companies.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No companies available
          </Text>
        ) : (
          companies.map((company) => (
            <Box
              key={company.id}
              onClick={() => onSelect(company)}
              style={{
                padding: "1rem",
                cursor: "pointer",
                borderRadius: "0.5rem",
                border: "var(--bios-border)"
              }}
              className={cx(commonClasses.hover, activeCompany.id === company.id ? commonClasses.activeBG : '')}
              mb="sm"
            >
              <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <IconBuilding size="1.5rem" style={{ flexShrink: 0 }} />
                <Box style={{ flex: 1 }}>
                  <Text fw={500} size="md">
                    {company.name}
                  </Text>
                  <Text size="sm" c="dimmed">
                    Created: {new Date(company.createdAt).toLocaleDateString()}
                  </Text>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    )
  })
}

export interface Region {
  name: 'USA' | 'UK' | 'Test' | 'Local'
  icon: string
  origin: string
  description: string
}

export const Regions = {
  USA: {
    name: "USA",
    icon: "🇺🇸",
    origin: "https://neurotune.us.bios.digital",
    description: "United States of America"
  } as Region,
  UK: {
    name: "UK",
    icon: "🇬🇧",
    origin: "https://neurotune.uk.bios.digital",
    description: "United Kingdom"
  } as Region,
  Test: {
    name: "Test",
    icon: "🧪",
    origin: "https://neurotune.test.bios.digital",
    description: "Test Environment"
  } as Region,
  Local: {
    name: "Local",
    icon: "💻",
    origin: "http://localhost:8000",
    description: "Local Development"
  } as Region
}
export const prodRegions = [Regions.UK, Regions.USA];
export const devRegions = [Regions.Test, Regions.Local];

export type Environment = 'Local' | 'Test' | 'UK' | 'USA';

export const getCurrentEnvironment = (): Environment => {
  const origin = window.location.origin;
  if (origin.startsWith("http://localhost:")) {
    return 'Local';
  }
  if (origin === Regions.Test.origin) {
    return 'Test';
  }
  if (origin === Regions.UK.origin) {
    return 'UK';
  }
  if (origin === Regions.USA.origin) {
    return 'USA';
  }
  // Default to Test for unknown origins in development, UK for production-like
  if (process.env.NODE_ENV !== 'production') {
    return 'Test';
  }
  return 'UK';
};

export const openRegionSelectionModal = (
  modalId: string,
  currentRegion: Region,
  onSelect: (region: Region) => void,
  userEmail?: string
) => {
  const regions = (userEmail && userEmail.endsWith("@bios.health")) ? [...prodRegions, ...devRegions] : prodRegions;
  return modals.open({
    modalId,
    title: "Select Region",
    size: "md",
    radius: "md",
    children: (
      <Box>
        {regions.length === 0 ? (
          <Text c="dimmed" ta="center" py="xl">
            No companies available
          </Text>
        ) : (
          regions.map((region) => (
            <Box
              key={region.name}
              onClick={() => onSelect(region)}
              className={cx(commonClasses.hover, region.name === currentRegion.name ? commonClasses.activeBG : '')}
              style={{
                padding: "1rem",
                cursor: "pointer",
                borderRadius: "0.5rem",
                transition: "background-color 0.2s ease",
                border: "var(--bios-border)"
              }}
              mb="sm"
            >
              <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                <Text size={"2em"}>{region.icon}</Text>
                <Box style={{ flex: 1 }}>
                  <Text fw={500} size="md">
                    {region.name}
                  </Text>
                  <Text size="sm" c="dimmed">
                    {region.description}
                  </Text>
                </Box>
              </Box>
            </Box>
          ))
        )}
      </Box>
    )
  })
}
